"use client"

import { cn } from "@/lib/utils"
import {
  DialogTitle,
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog"
import { useAtom } from "jotai"
import { isFullPageModalOpenAtom } from "@/app/contexts/Atoms"
import { VisuallyHidden } from "react-aria"
import { useEffect } from "react"
import useDismissModal from "./SubscribeModal/hooks/useDismissModal"
import { useRouter } from "next/navigation"

interface FullPageModalProps {
  /**
   * The content to be rendered inside the modal
   */
  children: React.ReactNode

  /**
   * Optional className to be applied to the modal content wrapper
   */
  className?: string

  /**
   * Optional callback when modal is dismissed
   */
  onDismiss?: () => void

  /**
   * Whether the modal is initially open
   * @default true
   */
  defaultOpen?: boolean

  /**
   * Title for screen readers
   */
  title?: string
}

/**
 * A full-page modal component with a close button.
 * Appears immediately at full screen without animations.
 * Built using shadcn/ui Dialog for accessibility.
 *
 * @example
 * ```tsx
 * <FullPageModal onDismiss={() => console.log('closed')}>
 *   <div>Modal content here</div>
 * </FullPageModal>
 * ```
 */
export default function FullPageModal({
  children,
  className,
  onDismiss,
  title,
}: FullPageModalProps) {
  const [isFullPageModalOpen, setIsFullPageModalOpen] = useAtom(
    isFullPageModalOpenAtom
  )
  const closeModal = useDismissModal("FullPageModal")
  const router = useRouter()

  // If this component loads, that means the full page modal is open. This component
  // only gets loaded when the user is confirmed to not have the cookie that indicates
  // they've dismissed it. So if we get here, assume the checks have already been done.
  useEffect(() => {
    setIsFullPageModalOpen(true)

    // Make sure to refresh the page on component dismount. If you X out of this, this isn't relevant
    // but if you try to click "Read it first" which doesn't properly close the modal, then when you
    // navigate back in the browser this component gets mounted again and pops up. If you refresh though
    // then this isn't a problem. See PAR-3953.
    return () => {
      router.refresh()
    }
  }, [])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onDismiss?.()
      closeModal()
    }
  }

  return (
    <Dialog open={isFullPageModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "max-w-full h-[100dvh] w-full p-0 gap-0 border-none translate-x-0 translate-y-0 !animate-none",
          "fixed inset-0",
          // Remove all animation classes
          className
        )}
      >
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>
        <div className="relative w-full h-full bg-card overflow-auto">
          <DialogClose
            className={cn(
              "absolute right-4 top-4 rounded-full",
              "hover:bg-gray-100 transition-colors"
            )}
            onClick={() => handleOpenChange(false)}
          ></DialogClose>

          <div className="h-full w-full">{children}</div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
